import { isArray, panic } from "../../Support";
import { SerializedComponent } from "../../components/SerializedApp";
import { makeText } from "../builder-utils";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeTextPropertyDescriptor } from "../components/descriptor-utils";
import {
    ActionDescription,
    ActionKind,
    getStringTypeOrStringTypeArrayColumns,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";
import { getFeatureSetting } from "../../feature-settings";
import { asMaybeString, asString, isPrimitiveValue } from "../../computation-model/data";
import { splitEmailAddresses } from "../../components/actions/deliver-email";
import { DeliverEmailFromActionBody } from "../../firebase-function-types";
import { getLocalizedString } from "../../components/localization";

interface DeliverEmailActionDescription extends ActionDescription {
    readonly recipient: PropertyDescription;
    // FIXME: Unify this with `AdditionalEmailProperties`
    readonly subject: PropertyDescription;
    readonly body: PropertyDescription;
    readonly replyTo: PropertyDescription | undefined;
}

export class DeliverEmailHandler extends BaseActionHandler<DeliverEmailActionDescription> {
    public readonly kind = ActionKind.DeliverEmail;
    // FIXME: proper icon
    public readonly iconName = "mail";

    public getDescriptor(
        _desc: DeliverEmailActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Send Email",
            isLegacy: !getFeatureSetting("deliverEmailAction"),
            needsScreenContext: true,
            quotaWarning:
                ccc.eminenceFlags.deliverEmailQuota <= 0
                    ? "Send Email is only supported in Private Pro apps."
                    : undefined,
            properties: [
                makeTextPropertyDescriptor("recipient", "Recipient", "Enter email address", true, mutatingScreenKind, {
                    preferredNames: ["recipient", "to", "email"],
                    preferredType: "email-address",
                    searchable: false,
                    columnFirst: true,
                    getAllowedColumns: getStringTypeOrStringTypeArrayColumns,
                }),
                makeTextPropertyDescriptor("subject", "Subject", "Enter subject line", true, mutatingScreenKind, {
                    preferredNames: ["subject", "title"],
                    searchable: false,
                    columnFirst: true,
                }),
                makeTextPropertyDescriptor("body", "Body", "Enter message body", true, mutatingScreenKind, {
                    preferredNames: ["body", "message"],
                    searchable: false,
                    columnFirst: true,
                }),
                makeTextPropertyDescriptor("replyTo", "Reply to", "Enter email address", true, mutatingScreenKind, {
                    preferredNames: ["replyto", "sender", "from"],
                    preferredType: "email-address",
                    searchable: false,
                    emptyByDefault: true,
                }),
            ],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: DeliverEmailActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        _outputContext: SerializedComponent,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _detailScreenTitle: SerializedComponent | undefined,
        _itemID: SerializedComponent | undefined,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for show toast action");
        }

        const recipient = makeText(builder, desc.recipient, tables.input, true, true, false, false, screenContext);
        const subject = makeText(builder, desc.subject, tables.input, true, true, false, false, screenContext);
        const body = makeText(builder, desc.body, tables.input, true, true, false, false, screenContext);
        const replyTo = makeText(builder, desc.replyTo, tables.input, true, true, false, false, screenContext);

        if (recipient === undefined || subject === undefined || body === undefined) return undefined;

        return b.action("DeliverEmail", {
            recipient,
            subject,
            body,
            replyTo,
            onSuccess,
            onFailure: b.showToast(b.localizedString("error"), false),
        });
    }

    public getTokenizedDescription(
        desc: DeliverEmailActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const columnToken = tokenForProperty(desc.recipient, tables, ccc);
        if (columnToken === undefined) return undefined;

        return [
            {
                kind: "string",
                value: "to ",
            },
            columnToken,
        ];
    }

    public inflate(ib: WireInflationBackend, desc: DeliverEmailActionDescription): WireActionHydrator | undefined {
        const { appID, appFacilities } = ib;
        const [recipientGetter] = ib.getValueGetterForProperty(desc.recipient, false);
        const [subjectGetter] = ib.getValueGetterForProperty(desc.subject, true);
        const [bodyGetter] = ib.getValueGetterForProperty(desc.body, true);
        const [replyToGetter] = ib.getValueGetterForProperty(desc.replyTo, false);

        return vp => {
            const recipientValue = recipientGetter(vp);
            const subject = asMaybeString(subjectGetter(vp));
            const messageBody = asMaybeString(bodyGetter(vp));
            const replyTo = asMaybeString(replyToGetter(vp));

            if (subject === undefined || messageBody === undefined) return undefined;

            const recipients: string[] = [];
            if (isArray(recipientValue)) {
                for (const v of recipientValue) {
                    recipients.push(...splitEmailAddresses(asString(v)));
                }
            } else if (isPrimitiveValue(recipientValue)) {
                recipients.push(...splitEmailAddresses(asString(recipientValue)));
            }

            if (recipients.length === 0) return undefined;

            return async ab => {
                const body: DeliverEmailFromActionBody = {
                    recipients,
                    subject,
                    body: messageBody,
                    replyTo,
                    appID,
                };
                const response = await appFacilities.callAuthIfAvailableCloudFunction(
                    "deliverEmailFromAction",
                    body,
                    {}
                );
                if (response?.ok !== true) {
                    ab.actionCallbacks.showToast(false, getLocalizedString("error"));
                    return false;
                }

                return true;
            };
        };
    }
}
